import { AsyncPipe, NgClass, NgFor, NgIf } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatOption, MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { FieldType } from '@ngx-formly/material';
import { Observable, Subject, asyncScheduler, debounceTime, distinctUntilChanged, map, of, switchMap, takeUntil } from 'rxjs';
import { isNil } from '@app/utils';
import { QuestionMarkMaskDirective } from '../../../directives/question-mark-mask.directive';

@Component({
	selector: 'formly-multiuse-search-input',
	templateUrl: './multiuse-search-input.component.html',
	styleUrls: ['./multiuse-search-input.component.less'],
	standalone: true,
	imports: [
		MatFormFieldModule,
		NgIf,
		NgClass,
		MatInputModule,
		MatAutocompleteModule,
		FormsModule,
		QuestionMarkMaskDirective,
		ReactiveFormsModule,
		FormlyModule,
		NgFor,
		MatOptionModule,
		MatIconModule,
		MatTooltipModule,
		MatAutocompleteTrigger,
		MatProgressSpinnerModule,
		AsyncPipe,
	],
})

/**
 * Multiuse search input component
 *
 * Custom Template Options:
 * @prop  dropdownProperties dropdownProperties: string[] (required) - defined Properties that correspond to the filtered object based off user input.
 * These  properties are used by the dropdownTemplate and at least one must be present.
 * @prop  dropdownTemplate: string (optional) - The template used for displaying data in the drop down list.
 * If not provided, the first property in dropdownProperties will be used.
 * @prop  inputTemplate: string (optional) - The template used to display in the input field when an option is selected from the dropdown list.
 * If not provided, the dropdownTemplate will be used or by default, the first property in dropdownProperties.
 * @prop  keyValueProperty: string (optional) - The property name within the filetered objects that will be used to set the control value when a filtered object is selected. Default is 'Id'.
 * @prop  defaultInputValue: string (optional) - the default value for the input field. This is mainly used when a value is not selected via filtered items.
 * @prop  appearance: string (optional) - The appearance of the input field. Default is 'outline'.
 * @prop  floatLabel: string (optional) - The float label of the input field. Default is 'auto'.
 * @prop  onOptionChanged: (option: any, field: FormlyFieldConfig) => void (optional) - Callback function that is called when an option is selected.
 * @prop  questionMarkMask: string (optional) - The mask to apply to the input field using the question mark mask directive. (this is specifically for ? masking)
 * @prop  questionMarkMaskUserInputOnly: boolean (optional) - used in combination with the questionMarkMask, if set to true, only the user input will follow the mask structure but will not impose ? for missing data.
 * @prop  useInputForValueChanges: boolean (optional) - Use the input field value for value changes. Filter Options are still allowed, but the value from the input field will be used, not the selected option.
 * A dropdownProperties array must be provided if useInputForValueChanges is true.  The first property in the dropdownProperties array will be used to store the input value.
 * @prop  filter: (search: object | string) => Observable<any[]> (optional) - The filter function that is used to filter the items based on the user input.
 * @prop  disabled: boolean (optional) - The disabled / readonly state of the input field.
 * @prop  readonly: boolean (optional) - The disabled / readonly state of the input field.
 * @prop  hideLabel: boolean (optional) - Hide the label of the input field.
 * @prop  onIconEvent: (event: any, field: FormlyFieldConfig) => void (optional) - Callback function that is called when the icon is clicked.
 * @prop  onFocus: (field: FormlyFieldConfig) => void (optional) - Callback function that is called when the input field is focused.
 * @prop  onBlur: (field: FormlyFieldConfig) => void (optional) - Callback function that is called when the input field is blurred.
 * @prop  subTextConfig: { propertyName: string; label: string } (optional) - Configuration for the subtext displayed below the input field.
 * It contains a propertyName to retrieve the value from the selected option and a label to display before the value.
 * @prop  showSubText: boolean (optional) - If true, the subtext will be displayed below the input field.
 * @prop  change: (field: FormlyFieldConfig) => void (optional) - Callback function that is called when the value of the field changes.
 * This is typically used to notify the parent component of the change in value.
 * @prop  icon: string (optional) - The icon to display in the input field. This is used in combination with the onIconEvent callback.
 * The icon will be displayed on the right side of the input field.
 * @prop  iconTooltip: string (optional) - The tooltip to display when hovering over the icon in the input field.
 * @prop  subTextConfig: { propertyName: string; label: string } (optional) - Configuration for the subtext displayed below the input field.
 * It contains a propertyName to retrieve the value from the selected option and a label to display before the value.
 *
 */
export class MultiuseSearchInputComponent extends FieldType<FormlyFieldConfig> implements OnInit, OnDestroy {
	@ViewChild(MatAutocompleteTrigger) autocompleteTrigger: MatAutocompleteTrigger;

	filteredItems$: Observable<any[]>;
	filteredItems: any[];
	parentIsModal = false;
	inputFormControl = new UntypedFormControl('');
	defaultInputValue = '';
	keyValueProperty = 'Id';
	optionSelectedValue: any;
	inputSubText: string;
	destroy$ = new Subject();
	cancelSearch = false;
	isSearching = false;

	constructor(private readonly changeDetectorRef: ChangeDetectorRef) {
		super();
	}

	public ngOnInit(): void {
		// Detect if component is inside a modal dialog
		this.parentIsModal = document.querySelectorAll('.linq-dialog').length > 0;

		this.changeDetectorRef.detectChanges();
		if (this.props?.filter) {
			this.filteredItems$ = this.inputFormControl.valueChanges.pipe(
				takeUntil(this.destroy$),
				distinctUntilChanged(),
				debounceTime(500),
				switchMap((search: object | string) => {
					if (this.cancelSearch) {
						return of([]);
					}

					this.isSearching = true;
					this.changeDetectorRef.detectChanges();

					return this.props.filter(search).pipe(
						map((items: any[]) => {
							this.isSearching = false;
							this.changeDetectorRef.detectChanges();
							this.filteredItems = items;
							return this.filteredItems.map((m) => ({ ...m, htmlDisplay: this.getOptionHtmlDisplayValue(m) }));
						}),
					);
				}),
			) as Observable<any[]>;
		}

		if (this.props?.keyValueProperty) {
			this.keyValueProperty = this.props.keyValueProperty;
		}

		if (this.props?.defaultInputValue !== undefined) {
			this.defaultInputValue = this.props.defaultInputValue;
			this.formControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: any) => {
				if (typeof value === 'object' && !isNil(value)) {
					this.cancelSearch = true;
					this.optionSelectedValue = value;
					this.inputFormControl.setValue(this.getSelectedDisplay(this.optionSelectedValue));
					this.inputSubText = this.getSubText(this.optionSelectedValue);
					const keyValue = this.optionSelectedValue[`${this.keyValueProperty}`];
					this.formControl.patchValue(keyValue, { emitEvent: false });
					this.value = keyValue;
					asyncScheduler.schedule(() => {
						this.cancelSearch = false;
					}, 800);
					return;
				} else if (!isNil(value)) {
					this.inputSubText = this.getSubText(this.optionSelectedValue);
				}

				if (isNil(value) && this.inputFormControl.value !== this.defaultInputValue) {
					this.inputFormControl.setValue(this.defaultInputValue);
					this.resetSubText();
				}
			});
		}

		if (this.props?.disabled) {
			this.inputFormControl.disable();
		}
		// monitor the form control status changes to disable the input field when the form control is disabled.
		this.formControl.statusChanges.subscribe(() => {
			if (this.formControl.disabled !== this.inputFormControl.disabled) {
				if (this.formControl.disabled) {
					this.inputFormControl.disable();
				} else {
					this.inputFormControl.enable();
				}
			}
		});
	}

	ngOnDestroy(): void {
		this.destroy$.next(undefined);
		this.destroy$.complete();
	}

	removeItem(): void {
		this.closeAutocomplete();
		this.formControl.setValue(null);
		this.inputFormControl.setValue(this.defaultInputValue);
	}

	onIconEvent(event: any): void {
		this.closeAutocomplete();
		if (this.props?.onIconEvent) {
			this.props.onIconEvent(event, this.field);
		}
	}

	onOptionChanged(selectedOption: MatOption): void {
		this.optionSelectedValue = selectedOption.value;
		this.cancelSearch = true;
		if (this.props.onOptionChanged) {
			this.props.onOptionChanged(this.optionSelectedValue, this.field);
		}
		if (this.props.change) {
			this.props.change(this.field);
		}

		this.inputFormControl.setValue(this.getSelectedDisplay(this.optionSelectedValue));

		const keyValue = this.optionSelectedValue[`${this.keyValueProperty}`];
		this.formControl.setValue(keyValue);
		this.value = keyValue;
		this.form.markAsDirty();
		asyncScheduler.schedule(() => {
			this.cancelSearch = false;
		}, 800);
	}

	onInputChanged(): void {
		if (this.props?.useInputForValueChanges === true) {
			const dropdownProperties = this.props?.dropdownProperties || [];
			if (dropdownProperties.length > 0) {
				const selectedValue = { [dropdownProperties[0]]: this.inputFormControl.value };
				this.optionSelectedValue = selectedValue;
				this.formControl.setValue(this.optionSelectedValue);
				this.value = this.optionSelectedValue;
				this.form.markAsDirty();
			}
		}
	}

	onFocus(): void {
		this.resetSubText();
		if (this.props?.onFocus) {
			this.props.onFocus(this.field);
		}
	}

	onBlur(): void {
		if (
			this.props?.filter &&
			this.props?.useInputForValueChanges !== true &&
			this.inputFormControl.value !== this.getSelectedDisplay(this.optionSelectedValue)
		) {
			// determine if the input value is in filtered items.
			// if so, set the form control to the selected value. otherwise, the form control should be null.
			// Setting the form control to null will in return clear the input field back to its default value.

			const foundItem = isNil(this.filteredItems)
				? null
				: this.filteredItems.find((item) => this.getSelectedDisplay(item) === this.inputFormControl.value);

			this.optionSelectedValue = isNil(foundItem) ? null : foundItem;
			this.formControl.setValue(isNil(foundItem) ? null : foundItem[`${this.keyValueProperty}`]);
			if (this.props.onOptionChanged) {
				this.props.onOptionChanged(foundItem, this.field);
			}

			if (isNil(foundItem)) {
				this.resetSubText();
			}
		} else {
			this.inputSubText = this.getSubText(this.optionSelectedValue);
		}

		this.formControl.markAllAsTouched();
	}

	closeAutocomplete() {
		if (this.autocompleteTrigger) {
			setTimeout(() => {
				this.autocompleteTrigger.closePanel();
			}, 100);
		}
	}

	private getSelectedDisplay(option: any): string {
		if (isNil(option)) {
			return '';
		}

		const inputTemplate = this.props?.inputTemplate || '';
		const dropdownProperties = this.props?.dropdownProperties || [];

		if (inputTemplate === '' || dropdownProperties.length === 0) {
			return this.getOptionHtmlDisplayValue(option);
		}

		let displayValue = inputTemplate;

		for (const propertyName of dropdownProperties) {
			displayValue = displayValue.replace(propertyName, option[propertyName]);
		}
		return displayValue;
	}

	private getOptionHtmlDisplayValue(option: any): string {
		const dropdownProperties = this.props?.dropdownProperties || [];
		const dropdownTemplate = this.props?.dropdownTemplate || '';

		if (dropdownProperties.length === 0) {
			return '';
		}

		if (dropdownTemplate === '') {
			return option[dropdownProperties[0]];
		} else {
			let displayValue = dropdownTemplate;

			for (const propertyName of dropdownProperties) {
				const index = displayValue.indexOf(propertyName);
				const brIndex = displayValue.indexOf('<br>');

				if (isNil(option[propertyName]) && brIndex > -1 && index > brIndex) {
					// if a template value comes after a <br> and is null.  Remove the <br> and all the text that follows.
					// An example for this is when displaying vendors and parent vendors. A Parent vendor will not have a parent.
					displayValue = displayValue.substring(0, brIndex);
				}

				displayValue = displayValue.replace(propertyName, option[propertyName]);
			}

			return displayValue;
		}
	}

	private getSubText(option: any): string {
		if (isNil(option)) {
			return null;
		}

		const subTextConfig = this.props?.subTextConfig;
		if (isNil(subTextConfig) || isNil(subTextConfig.propertyName) || isNil(subTextConfig.label)) {
			return null;
		}

		const subTextValue = option[subTextConfig.propertyName];
		if (isNil(subTextValue)) {
			return null;
		}

		return `${subTextConfig.label}${subTextValue}`;
	}

	private resetSubText(): void {
		this.inputSubText = null;
	}

	public get showSubText(): boolean {
		return this.props?.showSubText === true && (this.inputSubText ?? '').length > 0;
	}
}

export interface IMultiuseSearchInputProps {
	dropdownProperties: string[];
	dropdownTemplate?: string;
	inputTemplate?: string;
	keyValueProperty?: string;
	defaultInputValue?: string;
	appearance?: 'legacy' | 'standard' | 'fill' | 'outline';
	floatLabel?: 'always' | 'auto' | 'never';
	questionMarkMask?: string;
	questionMarkMaskUserInputOnly?: boolean;
	useInputForValueChanges?: boolean;
	disabled?: boolean;
	placeholder?: string;
	label?: string;
	readonly?: boolean;
	required?: boolean;
	hideLabel?: boolean;
	subTextConfig?: {
		propertyName: string;
		label: string;
	};
	showSubText?: boolean;
	icon?: string;
	iconTooltip?: string;
	filter: (search: object | string) => Observable<any[]>;
	change?: (field: FormlyFieldConfig) => void;
	onOptionChanged?: (option: any) => void;
	onIconEvent?: (event: any, field: FormlyFieldConfig) => void;
	onFocus?: (field: FormlyFieldConfig) => void;
	onBlur?: (field: FormlyFieldConfig) => void;
}
